import clsx from "clsx";
import dayjs from "dayjs";
import dateformat from "dateformat";
import SaveIcon from "@mui/icons-material/Save";
import UIButton from "@mui/material/Button";
import React, { useCallback, useMemo, useState } from "react";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import Edit2LineIcon from "remixicon-react/Edit2LineIcon";
import FindReplaceIcon from "@mui/icons-material/FindReplace";
import ArrowGoBackFillIcon from "remixicon-react/ArrowGoBackFillIcon";
import CalendarCheckFillIcon from "remixicon-react/CalendarCheckFillIcon";
import CheckboxCircleFillIcon from "remixicon-react/CheckboxCircleFillIcon";
import CloseCircleFillIcon from "remixicon-react/CloseCircleFillIcon";
import More2FillIcon from "remixicon-react/More2FillIcon";
import { IconButton, Menu, MenuItem } from "@mui/material";

import useModals from "./useModals";
import useAuth from "$hooks/useAuth";
import { theme } from "$utils/theme/bataillog";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import JTooltip from "$components/JTooltip/JTooltip";
import { useStyles } from "../styles/ProfileEDB.styles";
import { getEdbStatusColor, getEdbStatusLabel } from "$utils/typeConstants";
import { EDB_IS_DISABLED_MSG } from "$containers/EDB/constants";
import { Badge, Text, Button as LocalButton } from "$components";
import QuantityPicker from "$components/QuantityPicker/QuantityPicker";
import { useProfileEDBContext } from "$containers/EDB/ProfileEDB/context/ProfileEDBContext";
import {
  ProfileEDBTableProductRow,
  ProfileEDBTableTypes,
} from "../types/profileEDB.types";
import { PreparationOrderStatus } from "$types/PreparationOrder";
import { EdbStatus } from "$types/EDB";

export default function useProfileEDBTable(): ProfileEDBTableTypes {
  const classes = useStyles();
  const { isAdmin } = useAuth();
  const {
    idEdb,
    setVal,
    setProductsQuantityMap,
    currentTab,
    isModifyingEdb,
    setIsDeleteEdb,
    setIsCancelEdb,
    setIsDuplicateEdb,
    isDeliveryEditable,
    productsQuantityMap,
    isModifyingDelivery,
    hasAPreparationOrderValidated,

    edb,
    opList,
    statusEDB,
    isDisabled,
    isOpListLoaded,
    setSelectedSubstituteProduct,
    setOpenModalSubstituteProduct,

    canApprove,
    setIsApprovalModalOpen,

    getProductInOP,
    setIsModifyingEdb,
    notEditableProducts,
    setProductToDeleteId,
    setIsModifyingDelivery,
    setOpenModalDeleteProduct,
  } = useProfileEDBContext();

  const { updateProducts, handleChangeProductQty } = useModals();

  const isProductEditable = useCallback(
    (p: any) =>
      notEditableProducts.findIndex(
        (prod) => prod.productId === p.relatedProduct._id
      ) === -1,
    [notEditableProducts]
  );

  const resetMapValue = useCallback(
    (edbProductId: string) => {
      if (edb) {
        const edbProduct = edb.products.find(
          (pr: any) => pr._id === edbProductId
        );
        if (edbProduct) {
          setVal(edbProduct.qty);
          productsQuantityMap.delete(edbProductId);
          setProductsQuantityMap(productsQuantityMap);
        }
      }
    },
    [edb, productsQuantityMap, setProductsQuantityMap, setVal]
  );

  const getEdbDateLastModification = useCallback(() => {
    if (edb && edb.createdAt) {
      if (edb.updatedAt) {
        if (edb.updatedAt === edb.createdAt) {
          return `Créée le ${dateformat(
            new Date(edb?.createdAt),
            DEFAULT_DATE_FORMAT
          )}`;
        }
        return `Modifiée le ${dateformat(
          new Date(edb?.updatedAt),
          DEFAULT_DATE_FORMAT
        )}`;
      }
      return `Créée le ${dateformat(
        new Date(edb?.createdAt),
        DEFAULT_DATE_FORMAT
      )}`;
    }
    return "";
  }, [edb]);

  const showModifier =
    currentTab === 2 &&
    isAdmin() &&
    isDeliveryEditable &&
    isOpListLoaded &&
    !hasAPreparationOrderValidated &&
    !isModifyingDelivery;

  const showCancelEdb =
    isAdmin() &&
    isOpListLoaded &&
    !edb?.isDisable &&
    !isDisabled &&
    statusEDB !== EdbStatus.COLLECTED;

  const showDeleteEdb =
    isAdmin() &&
    isOpListLoaded &&
    !opList?.find(
      (op) =>
        op.edb._id === idEdb && op.status !== PreparationOrderStatus.CANCELLED
    );

  const showDuplicateEdb =
    isAdmin() &&
    isOpListLoaded &&
    !edb?.isDisable &&
    !isDisabled &&
    (statusEDB === EdbStatus.CART_CREATED ||
      statusEDB === EdbStatus.CART_VALIDATED);

  const hasActionButtons =
    showModifier || showDuplicateEdb || showCancelEdb || showDeleteEdb;

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const renderHead = () => {
    const isApproved = edb?.approvedAt && edb?.status !== EdbStatus.REFUSED;
    const isRefused = edb?.status === EdbStatus.REFUSED;
    const isWaiting = edb?.status === EdbStatus.WAITING;

    return (
      <div className={classes.customHeader}>
        <div
          className={classes.calendar}
          style={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <CalendarCheckFillIcon className={classes.calendarIcon} />
            {getEdbDateLastModification()}
          </div>
          {isApproved && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: 14,
                color: "#7A869A",
                paddingLeft: 2,
              }}
            >
              <CheckboxCircleFillIcon
                style={{ width: 18, marginRight: 8, flexShrink: 0 }}
              />
              Approuvée le {dayjs(edb.approvedAt).format("DD/MM/YYYY")}
            </div>
          )}
          {isRefused && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: 14,
                color: "#7A869A",
                paddingLeft: 2,
              }}
            >
              <CloseCircleFillIcon
                style={{ width: 18, marginRight: 8, flexShrink: 0 }}
              />
              Refusée le {dayjs(edb.refusedAt).format("DD/MM/YYYY")}
            </div>
          )}
          {isWaiting && canApprove && !edb?.isDisable && (
            <div style={{ paddingLeft: 2, marginTop: 2 }}>
              <LocalButton
                variant="contained"
                color="primary"
                disableElevation
                onClick={() => setIsApprovalModalOpen(true)}
                style={{ minHeight: 30, fontSize: 13, padding: "4px 12px" }}
              >
                Approbation
              </LocalButton>
            </div>
          )}
        </div>
        <div className={classes.badge}>
          <Badge colorManual={getEdbStatusColor(statusEDB, theme, isDisabled)}>
            {getEdbStatusLabel(statusEDB)}
          </Badge>
        </div>

        {hasActionButtons && (
          <div className={classes.buttonHeader}>
            <LocalButton
              variant="contained"
              color="primary"
              disableElevation
              onClick={handleOpenMenu}
              aria-controls={menuOpen ? "edb-actions-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={menuOpen ? "true" : "false"}
              aria-label="Actions EDB"
              style={{ minHeight: 37 }}
            >
              <More2FillIcon />
            </LocalButton>
            <Menu
              id="edb-actions-menu"
              anchorEl={menuAnchor}
              open={menuOpen}
              onClose={handleCloseMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  minWidth: 150,
                  mt: 0,
                  "& .MuiMenuItem-root": {
                    justifyContent: "flex-start",
                    "&:hover": {
                      backgroundColor: "#e0e0e0",
                    },
                  },
                },
              }}
            >
              {showDuplicateEdb && (
                <MenuItem
                  onClick={() => {
                    handleCloseMenu();
                    setIsDuplicateEdb(true);
                  }}
                  disabled={isDisabled}
                  title={isDisabled ? EDB_IS_DISABLED_MSG : undefined}
                >
                  Dupliquer l&apos;EDB
                </MenuItem>
              )}
              {showModifier && (
                <MenuItem
                  onClick={() => {
                    handleCloseMenu();
                    setIsModifyingDelivery(true);
                  }}
                  disabled={isDisabled}
                  title={isDisabled ? EDB_IS_DISABLED_MSG : undefined}
                >
                  Modifier
                </MenuItem>
              )}
              {showCancelEdb && (
                <MenuItem
                  onClick={() => {
                    handleCloseMenu();
                    setIsCancelEdb(true);
                  }}
                >
                  Annuler l&apos;EDB
                </MenuItem>
              )}
              {showDeleteEdb && (
                <MenuItem
                  onClick={() => {
                    handleCloseMenu();
                    setIsDeleteEdb(true);
                  }}
                >
                  Supprimer l&apos;EDB
                </MenuItem>
              )}
            </Menu>
          </div>
        )}
      </div>
    );
  };

  const renderProduct = useCallback(() => {
    const data: ProfileEDBTableProductRow[][] = [];

    edb?.products
      ?.filter((p: any) => !p.receptionReferenceId)
      .forEach((p: any) => {
        const row: ProfileEDBTableProductRow[] = [
          {
            value: <Text>{p.relatedProduct.reference}</Text>,
            customCellClass: clsx(classes.customCell),
          },
          {
            value: <Text type="important">{p.relatedProduct.designation}</Text>,
            customCellClass: clsx(classes.customCell),
          },
          {
            value: <Text>{p.relatedProduct.unit}</Text>,
            customCellClass: clsx(classes.customCell),
          },
          {
            value: (
              <div>
                {isModifyingEdb && !isDisabled ? (
                  <JTooltip
                    title={
                      (productsQuantityMap.get(p._id) ?? p.qty) ===
                        getProductInOP(p.relatedProduct._id) &&
                      getProductInOP(p.relatedProduct._id) !== 0
                        ? `La quantité ne peut pas être inférieure à ${getProductInOP(
                            p.relatedProduct._id
                          )} car la référence a déjà cette quantité en cours de préparation`
                        : undefined
                    }
                  >
                    <div className={classes.quantityEditRow}>
                      <div className={classes.quantityPickerWrapper}>
                        <QuantityPicker
                          className={classes.quantityPicker}
                          product={p}
                          minValue={
                            getProductInOP(p.relatedProduct._id) &&
                            getProductInOP(p.relatedProduct._id) !== 0
                              ? getProductInOP(p.relatedProduct._id)
                              : 1
                          }
                          onQuantityChange={(quantity) =>
                            handleChangeProductQty(quantity, p)
                          }
                          quantity={productsQuantityMap.get(p._id) ?? p.qty}
                          size="small"
                        />
                      </div>
                      {productsQuantityMap.has(p._id) && (
                        <IconButton
                          onClick={() => {
                            resetMapValue(p._id);
                          }}
                          className={classes.resetQtyButton}
                        >
                          <ArrowGoBackFillIcon
                            style={{
                              minWidth: 30,
                              color: theme.color.blueLight,
                            }}
                          />
                        </IconButton>
                      )}
                    </div>
                  </JTooltip>
                ) : (
                  <Text>{p.qty}</Text>
                )}
              </div>
            ),
            customCellClass: clsx(classes.customCell),
          },
          {
            value: <div>{p.productReplaced?.reference ?? "-"}</div>,
            customCellClass: clsx(classes.customCell),
          },
          {
            value: (
              <div>
                <UIButton
                  onClick={() => {
                    setOpenModalSubstituteProduct(true);
                    setSelectedSubstituteProduct(p);
                  }}
                  disabled={
                    !isAdmin() ||
                    isModifyingEdb ||
                    !isProductEditable(p) ||
                    isDisabled
                  }
                  style={{
                    opacity:
                      !isAdmin() ||
                      isModifyingEdb ||
                      !isProductEditable(p) ||
                      isDisabled
                        ? 0.9
                        : 1,
                    cursor:
                      !isAdmin() ||
                      isModifyingEdb ||
                      !isProductEditable(p) ||
                      isDisabled
                        ? "not-allowed"
                        : "pointer",
                    pointerEvents:
                      !isAdmin() ||
                      isModifyingEdb ||
                      !isProductEditable(p) ||
                      isDisabled
                        ? "none"
                        : "auto",
                  }}
                >
                  <FindReplaceIcon
                    style={{
                      minWidth: 30,
                      color:
                        !isAdmin() ||
                        isModifyingEdb ||
                        !isProductEditable(p) ||
                        isDisabled
                          ? theme.color.gray3
                          : theme.color.blueLight,
                    }}
                  />
                </UIButton>
              </div>
            ),
          },
          {
            value: (
              <div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <UIButton
                    onClick={() => {
                      setProductToDeleteId(p.relatedProduct._id);
                      setOpenModalDeleteProduct(true);
                    }}
                    disabled={
                      !isAdmin() ||
                      isModifyingEdb ||
                      !isProductEditable(p) ||
                      isDisabled
                    }
                    style={{
                      opacity:
                        !isAdmin() ||
                        isModifyingEdb ||
                        !isProductEditable(p) ||
                        isDisabled
                          ? 0.9
                          : 1,
                      cursor:
                        !isAdmin() ||
                        isModifyingEdb ||
                        !isProductEditable(p) ||
                        isDisabled
                          ? "not-allowed"
                          : "pointer",
                      pointerEvents:
                        !isAdmin() ||
                        isModifyingEdb ||
                        !isProductEditable(p) ||
                        isDisabled
                          ? "none"
                          : "auto",
                    }}
                  >
                    <DeleteIcon
                      style={{
                        minWidth: 30,
                        color:
                          !isAdmin() ||
                          isModifyingEdb ||
                          !isProductEditable(p) ||
                          isDisabled
                            ? theme.color.gray3
                            : theme.color.blueLight,
                      }}
                    />
                  </UIButton>
                </div>
              </div>
            ),
            customCellClass: clsx(classes.customCell),
          },
        ];
        data.push(row);
      });
    return data;
  }, [
    edb?.products,
    classes.customCell,
    classes.quantityEditRow,
    classes.quantityPicker,
    classes.quantityPickerWrapper,
    classes.resetQtyButton,
    isModifyingEdb,
    isDisabled,
    productsQuantityMap,
    getProductInOP,
    isAdmin,
    isProductEditable,
    handleChangeProductQty,
    resetMapValue,
    setOpenModalSubstituteProduct,
    setSelectedSubstituteProduct,
    setProductToDeleteId,
    setOpenModalDeleteProduct,
  ]);

  const tableHeader = useMemo(
    () => [
      { id: "référence", label: "référence", width: 170 },
      { id: "désignation", label: "désignation", width: 340 },
      {
        id: "unite",
        label: "unité",
        width: 140,
      },
      {
        id: "qty",
        label: (
          <div style={{ display: "flex", alignItems: "center" }}>
            <span>Quantité</span>
            <UIButton
              className={classes.blueIcon}
              style={{ marginLeft: 5, width: 35 }}
              onClick={() => {
                if (isModifyingEdb) {
                  updateProducts();
                }
                setIsModifyingEdb(!isModifyingEdb);
              }}
              disabled={!isAdmin() || isDisabled}
              title={isDisabled ? EDB_IS_DISABLED_MSG : undefined}
            >
              {isModifyingEdb ? <SaveIcon /> : <Edit2LineIcon />}
            </UIButton>
          </div>
        ),
        width: 50,
      },
      {
        id: "substitution",
        label: "En remplacement de",
        width: 200,
      },
      {
        id: "substitute",
        label: "Substituer",
        width: 100,
      },
      {
        id: "delete",
        label: "",
        width: 100,
      },
    ],
    [
      classes.blueIcon,
      isAdmin,
      isModifyingEdb,
      isDisabled,
      setIsModifyingEdb,
      updateProducts,
    ]
  );

  return { tableHeader, renderHead, renderProduct };
}
